/**
 * ModalExamples.tsx
 * Comprehensive examples of HeroUI Modal component
 * 
 * Showcases all HeroUI Modal features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Input } from '@enfomentis/ui';
import { TextArea } from '@enfomentis/ui';
import { Card, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { 
  AlertTriangle,
  Settings,
  Trash2,
  Edit3,
  Save,
  Eye,
  Download,
  DollarSign,
  TrendingUp,
  Activity
} from 'lucide-react';

export const ModalExamples: React.FC = () => {
  // Basic modals
  const { isOpen: isBasicOpen, onOpen: onBasicOpen, onOpenChange: onBasicOpenChange } = useDisclosure();
  const { isOpen: isSizesOpen, onOpen: onSizesOpen, onOpenChange: onSizesOpenChange } = useDisclosure();
  const { isOpen: isPlacementOpen, onOpen: onPlacementOpen, onOpenChange: onPlacementOpenChange } = useDisclosure();
  const { isOpen: isBackdropOpen, onOpen: onBackdropOpen, onOpenChange: onBackdropOpenChange } = useDisclosure();
  
  // Interactive modals
  const { isOpen: isFormOpen, onOpen: onFormOpen, onOpenChange: onFormOpenChange } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onOpenChange: onConfirmOpenChange } = useDisclosure();
  const { isOpen: isScrollOpen, onOpen: onScrollOpen, onOpenChange: onScrollOpenChange } = useDisclosure();
  const { isOpen: isNonDismissOpen, onOpen: onNonDismissOpen, onOpenChange: onNonDismissOpenChange } = useDisclosure();

  // Trading-specific modals
  const { isOpen: isTradeOpen, onOpen: onTradeOpen, onOpenChange: onTradeOpenChange } = useDisclosure();
  const { isOpen: isAnalysisOpen, onOpen: onAnalysisOpen, onOpenChange: onAnalysisOpenChange } = useDisclosure();
  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onOpenChange: onSettingsOpenChange } = useDisclosure();

  // Modal variants state
  const [selectedSize, setSelectedSize] = useState<'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full'>('md');
  const [selectedPlacement, setSelectedPlacement] = useState<'auto' | 'top' | 'center' | 'bottom'>('center');
  const [selectedBackdrop, setSelectedBackdrop] = useState<'transparent' | 'opaque' | 'blur'>('opaque');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleFormSubmit = () => {
    console.log('Form submitted:', formData);
    onFormOpenChange();
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Modal</h3>
          <div className="flex gap-4">
            <Button onPress={onBasicOpen} color="primary">Open Basic Modal</Button>
          </div>

          <Modal isOpen={isBasicOpen} onOpenChange={onBasicOpenChange}>
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader className="flex flex-col gap-1">
                    Basic Modal
                  </ModalHeader>
                  <ModalBody>
                    <p>This is a basic modal example with header, body, and footer sections.</p>
                    <p className="text-text-secondary">You can include any content here such as text, forms, or other components.</p>
                  </ModalBody>
                  <ModalFooter>
                    <Button color="danger" variant="light" onPress={onClose}>
                      Cancel
                    </Button>
                    <Button color="primary" onPress={onClose}>
                      Action
                    </Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Modal Sizes</h3>
          <div className="flex flex-wrap gap-2">
            {(['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', 'full'] as const).map((size) => (
              <Button
                key={size}
                size="sm"
                variant={selectedSize === size ? 'solid' : 'bordered'}
                color="primary"
                onPress={() => {
                  setSelectedSize(size);
                  onSizesOpen();
                }}
              >
                {size.toUpperCase()}
              </Button>
            ))}
          </div>

          <Modal size={selectedSize} isOpen={isSizesOpen} onOpenChange={onSizesOpenChange}>
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader>
                    Modal Size: {selectedSize.toUpperCase()}
                  </ModalHeader>
                  <ModalBody>
                    <p>This modal is using size: <code className="bg-surface px-2 py-1 rounded text-sm">{selectedSize}</code></p>
                    <p className="text-text-secondary">Modal sizes range from 'xs' (extra small) to 'full' (full screen).</p>
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="light" onPress={onClose}>Close</Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Modal Placement</h3>
          <div className="flex gap-2">
            {(['auto', 'top', 'center', 'bottom'] as const).map((placement) => (
              <Button
                key={placement}
                size="sm"
                variant={selectedPlacement === placement ? 'solid' : 'bordered'}
                color="secondary"
                onPress={() => {
                  setSelectedPlacement(placement);
                  onPlacementOpen();
                }}
              >
                {placement.charAt(0).toUpperCase() + placement.slice(1)}
              </Button>
            ))}
          </div>

          <Modal placement={selectedPlacement} isOpen={isPlacementOpen} onOpenChange={onPlacementOpenChange}>
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader>
                    Placement: {selectedPlacement.charAt(0).toUpperCase() + selectedPlacement.slice(1)}
                  </ModalHeader>
                  <ModalBody>
                    <p>This modal is positioned with placement: <code className="bg-surface px-2 py-1 rounded text-sm">{selectedPlacement}</code></p>
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="light" onPress={onClose}>Close</Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Backdrop Variants</h3>
          <div className="flex gap-2">
            {(['transparent', 'opaque', 'blur'] as const).map((backdrop) => (
              <Button
                key={backdrop}
                size="sm"
                variant={selectedBackdrop === backdrop ? 'solid' : 'bordered'}
                color="success"
                onPress={() => {
                  setSelectedBackdrop(backdrop);
                  onBackdropOpen();
                }}
              >
                {backdrop.charAt(0).toUpperCase() + backdrop.slice(1)}
              </Button>
            ))}
          </div>

          <Modal backdrop={selectedBackdrop} isOpen={isBackdropOpen} onOpenChange={onBackdropOpenChange}>
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader>
                    Backdrop: {selectedBackdrop.charAt(0).toUpperCase() + selectedBackdrop.slice(1)}
                  </ModalHeader>
                  <ModalBody>
                    <p>This modal uses <code className="bg-surface px-2 py-1 rounded text-sm">{selectedBackdrop}</code> backdrop.</p>
                    <p className="text-text-secondary">Different backdrop styles create different visual effects.</p>
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="light" onPress={onClose}>Close</Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Form Modal</h3>
          <Button onPress={onFormOpen} color="primary" startContent={<Edit3 className="w-4 h-4" />}>
            Open Form Modal
          </Button>

          <Modal isOpen={isFormOpen} onOpenChange={onFormOpenChange}>
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader className="flex flex-col gap-1">
                    <h4 className="font-semibold">Contact Form</h4>
                    <p className="text-sm text-text-secondary">Send us a message</p>
                  </ModalHeader>
                  <ModalBody>
                    <div className="space-y-4">
                      <Input
                        label="Name"
                        placeholder="Your name"
                        value={formData.name}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, name: value }))}
                      />
                      <Input
                        label="Email"
                        placeholder="your.email@example.com"
                        type="email"
                        value={formData.email}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, email: value }))}
                      />
                      <TextArea
                        label="Message"
                        placeholder="Your message..."
                        value={formData.message}
                        onValueChange={(value: string) => setFormData(prev => ({ ...prev, message: value }))}
                        minRows={3}
                      />
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button color="danger" variant="light" onPress={onClose}>
                      Cancel
                    </Button>
                    <Button 
                      color="primary" 
                      onPress={handleFormSubmit}
                      startContent={<Save className="w-4 h-4" />}
                    >
                      Send Message
                    </Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Confirmation Modal</h3>
          <Button onPress={onConfirmOpen} color="danger" startContent={<Trash2 className="w-4 h-4" />}>
            Delete Item
          </Button>

          <Modal isOpen={isConfirmOpen} onOpenChange={onConfirmOpenChange}>
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-warning" />
                    Confirm Deletion
                  </ModalHeader>
                  <ModalBody>
                    <p>Are you sure you want to delete this item? This action cannot be undone.</p>
                    <div className="bg-danger-50 dark:bg-danger-900/20 p-3 rounded-lg mt-3">
                      <p className="text-sm text-danger">
                        <strong>Warning:</strong> All associated data will be permanently removed.
                      </p>
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="light" onPress={onClose}>
                      Cancel
                    </Button>
                    <Button 
                      color="danger" 
                      onPress={onClose}
                      startContent={<Trash2 className="w-4 h-4" />}
                    >
                      Delete
                    </Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Scrollable Content</h3>
          <Button onPress={onScrollOpen} color="secondary" startContent={<Eye className="w-4 h-4" />}>
            View Long Content
          </Button>

          <Modal isOpen={isScrollOpen} onOpenChange={onScrollOpenChange} scrollBehavior="inside">
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader>Long Content Modal</ModalHeader>
                  <ModalBody>
                    <div className="space-y-4">
                      <p>This modal contains long content that requires scrolling.</p>
                      {Array.from({ length: 20 }, (_, i) => (
                        <Card key={i}>
                          <CardBody>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                                <span className="text-sm font-semibold text-primary">{i + 1}</span>
                              </div>
                              <div>
                                <h5 className="font-semibold">Item {i + 1}</h5>
                                <p className="text-sm text-text-secondary">
                                  This is item number {i + 1} in the scrollable list.
                                </p>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      ))}
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="light" onPress={onClose}>Close</Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Non-dismissible Modal</h3>
          <Button onPress={onNonDismissOpen} color="warning">
            Open Loading Modal
          </Button>

          <Modal isOpen={isNonDismissOpen} onOpenChange={onNonDismissOpenChange} isDismissable={false} hideCloseButton>
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader>Processing...</ModalHeader>
                  <ModalBody>
                    <div className="flex items-center gap-3">
                      <div className="animate-spin">
                        <Activity className="w-5 h-5 text-primary" />
                      </div>
                      <p>Please wait while we process your request. This may take a few moments.</p>
                    </div>
                    <div className="mt-4">
                      <div className="w-full bg-surface rounded-full h-2">
                        <div className="bg-primary h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                      </div>
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button color="primary" onPress={onClose}>
                      Force Close (Demo)
                    </Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading-Specific Modals</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Button onPress={onTradeOpen} color="success" className="h-16 flex-col">
              <TrendingUp className="w-5 h-5 mb-1" />
              <span>New Trade</span>
            </Button>
            
            <Button onPress={onAnalysisOpen} color="primary" className="h-16 flex-col">
              <Activity className="w-5 h-5 mb-1" />
              <span>Market Analysis</span>
            </Button>
            
            <Button onPress={onSettingsOpen} color="secondary" className="h-16 flex-col">
              <Settings className="w-5 h-5 mb-1" />
              <span>Trading Settings</span>
            </Button>
          </div>

          {/* New Trade Modal */}
          <Modal isOpen={isTradeOpen} onOpenChange={onTradeOpenChange} size="lg">
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-success" />
                    New Trade Entry
                  </ModalHeader>
                  <ModalBody>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input label="Symbol" placeholder="AAPL" />
                      <Input label="Quantity" type="number" placeholder="100" />
                      <Input label="Entry Price" type="number" placeholder="150.00" startContent="$" />
                      <Input label="Stop Loss" type="number" placeholder="145.00" startContent="$" />
                      <Input label="Take Profit" type="number" placeholder="160.00" startContent="$" />
                      <div>
                        <label className="text-sm font-medium text-text-primary mb-2 block">Trade Type</label>
                        <div className="flex gap-2">
                          <Chip color="success" variant="flat">Long</Chip>
                          <Chip color="danger" variant="bordered">Short</Chip>
                        </div>
                      </div>
                    </div>
                    <TextArea
                      label="Trade Notes"
                      placeholder="Analysis, strategy, or other notes..."
                      className="mt-4"
                      minRows={2}
                    />
                  </ModalBody>
                  <ModalFooter>
                    <Button color="danger" variant="light" onPress={onClose}>
                      Cancel
                    </Button>
                    <Button color="success" onPress={onClose} startContent={<Save className="w-4 h-4" />}>
                      Execute Trade
                    </Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>

          {/* Market Analysis Modal */}
          <Modal isOpen={isAnalysisOpen} onOpenChange={onAnalysisOpenChange} size="2xl">
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary" />
                    Market Analysis Dashboard
                  </ModalHeader>
                  <ModalBody>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card>
                          <CardBody className="text-center">
                            <DollarSign className="w-8 h-8 text-success mx-auto mb-2" />
                            <h4 className="font-semibold text-lg">$12,450</h4>
                            <p className="text-sm text-text-secondary">Portfolio Value</p>
                            <Chip size="sm" color="success" className="mt-2">+2.4%</Chip>
                          </CardBody>
                        </Card>
                        
                        <Card>
                          <CardBody className="text-center">
                            <Activity className="w-8 h-8 text-primary mx-auto mb-2" />
                            <h4 className="font-semibold text-lg">89</h4>
                            <p className="text-sm text-text-secondary">Active Positions</p>
                            <Badge content="15" color="primary" size="sm" className="mt-2">
                              <Chip size="sm" color="primary" variant="flat">New</Chip>
                            </Badge>
                          </CardBody>
                        </Card>
                        
                        <Card>
                          <CardBody className="text-center">
                            <TrendingUp className="w-8 h-8 text-warning mx-auto mb-2" />
                            <h4 className="font-semibold text-lg">76%</h4>
                            <p className="text-sm text-text-secondary">Win Rate</p>
                            <Chip size="sm" color="success" className="mt-2">Excellent</Chip>
                          </CardBody>
                        </Card>
                      </div>

                      <div className="h-48 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/10 dark:to-indigo-900/20 rounded-lg flex items-center justify-center">
                        <div className="text-center">
                          <Activity className="w-12 h-12 text-blue-500 mx-auto mb-2" />
                          <p className="text-text-secondary">Advanced Chart Analysis</p>
                          <p className="text-sm text-text-secondary">Technical indicators and market trends</p>
                        </div>
                      </div>
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="light" onPress={onClose}>
                      Close
                    </Button>
                    <Button color="primary" startContent={<Download className="w-4 h-4" />}>
                      Export Report
                    </Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>

          {/* Trading Settings Modal */}
          <Modal isOpen={isSettingsOpen} onOpenChange={onSettingsOpenChange} size="lg">
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-secondary" />
                    Trading Settings
                  </ModalHeader>
                  <ModalBody>
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-semibold mb-3">Risk Management</h4>
                        <div className="space-y-3">
                          <Input label="Max Position Size" type="number" placeholder="10000" startContent="$" />
                          <Input label="Daily Loss Limit" type="number" placeholder="500" startContent="$" />
                          <Input label="Risk per Trade (%)" type="number" placeholder="2" endContent="%" />
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold mb-3">Trading Hours</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <Input label="Market Open" type="time" defaultValue="09:30" />
                          <Input label="Market Close" type="time" defaultValue="16:00" />
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold mb-3">Notifications</h4>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span>Trade Executions</span>
                            <Chip color="success" size="sm">Enabled</Chip>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Price Alerts</span>
                            <Chip color="success" size="sm">Enabled</Chip>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Daily Reports</span>
                            <Chip color="default" size="sm">Disabled</Chip>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button color="danger" variant="light" onPress={onClose}>
                      Cancel
                    </Button>
                    <Button color="primary" onPress={onClose} startContent={<Save className="w-4 h-4" />}>
                      Save Settings
                    </Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
        </div>
      </div>
    </PlaygroundPageLayout>
  );
};